import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  TextField,
  Box,
  Typography,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  FormGroup,
  Autocomplete,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import apiClient from '../api/client';
import { getCommodityValues } from '../api/images';

// Default columns that match the search page display
const DEFAULT_COLUMNS = [
  { field: 'gtin', label: 'GTIN', default: true },
  { field: 'status', label: 'Status', default: true },
  { field: 'last_modified_by_email', label: 'Last Created By', default: true },
  { field: 'is_submitted', label: 'Submitted', default: true },
  { field: 'scheduled', label: 'Scheduled', default: true },
  { field: 'created_at', label: 'Created Date', default: true },
  { field: 'updated_at', label: 'Updated Date', default: true },
];

// Additional available columns from the database
const ADDITIONAL_COLUMNS = [
  { field: 'id', label: 'Image ID', default: false },
  { field: 'original_gtin', label: 'Original GTIN', default: false },
  { field: 'user_email', label: 'Original Created By', default: false },
  { field: 'user_username', label: 'Username', default: false },
  { field: 'commodity', label: 'Commodity', default: false },
  { field: 'sub_commodity', label: 'Sub-Commodity', default: false },
  { field: 'is_staged', label: 'Staged', default: false },
  { field: 'is_archived', label: 'Archived', default: false },
  { field: 'submitted_at', label: 'Submitted Date', default: false },
  { field: 'reason', label: 'Failure Reason', default: false },
  { field: 'product_description', label: 'Product Description', default: false },
  { field: 'source_urls', label: 'Source URLs', default: false },
  { field: 'file_path', label: 'File Path', default: false },
  { field: 'thumbnail_path', label: 'Thumbnail Path', default: false },
  { field: 'gtin_normalization_warning', label: 'GTIN Warning', default: false },
  { field: 'product_badges', label: 'Product Badges', default: false },
  { field: 'current_badges', label: 'Current Badges', default: false },
  { field: 'badge_submitted_date', label: 'Badge Submitted Date', default: false },
  { field: 'badge_removal_date', label: 'Badge Removal Date', default: false },
  { field: 'badge_removal_status', label: 'Badge Removal Status', default: false },
];

const ReportGenerationDialog = ({ 
  open, 
  onClose, 
  filters = {}, 
  onGenerate 
}) => {
  const [format, setFormat] = useState('csv');
  const [title, setTitle] = useState('Image Report');
  const [includeSummary, setIncludeSummary] = useState(true);
  const [selectedColumns, setSelectedColumns] = useState({});
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [commodityOptions, setCommodityOptions] = useState({ commodities: [], sub_commodities: [] });
  
  // Editable filter states - initialize from props
  const [reportFilters, setReportFilters] = useState({
    status: '',
    gtin_search: '',
    commodity: '',
    sub_commodity: '',
    created_after: '',
    created_before: '',
    is_staged: null,
    is_submitted: null,
    is_archived: null
  });

  // All available columns
  const allColumns = [...DEFAULT_COLUMNS, ...ADDITIONAL_COLUMNS];

  useEffect(() => {
    if (open) {
      // Initialize selected columns - default ones are checked
      const initialSelection = {};
      allColumns.forEach(col => {
        initialSelection[col.field] = col.default;
      });
      setSelectedColumns(initialSelection);
      setError(null);
      
      // Initialize report filters from props
      setReportFilters({
        status: filters.status || '',
        gtin_search: filters.gtin_search || '',
        commodity: filters.commodity || '',
        sub_commodity: filters.sub_commodity || '',
        created_after: filters.created_after || '',
        created_before: filters.created_before || '',
        is_staged: filters.is_staged || null,
        is_submitted: filters.is_submitted || null,
        is_archived: filters.is_archived || null
      });
      
      // Fetch commodity options
      fetchCommodityOptions();
    }
  }, [open, filters]);
  
  const fetchCommodityOptions = async () => {
    try {
      const data = await getCommodityValues();
      setCommodityOptions(data);
    } catch (error) {
      console.error('Failed to fetch commodity values:', error);
    }
  };

  const handleColumnToggle = (field) => {
    setSelectedColumns(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSelectAll = () => {
    const newSelection = {};
    allColumns.forEach(col => {
      newSelection[col.field] = true;
    });
    setSelectedColumns(newSelection);
  };

  const handleDeselectAll = () => {
    const newSelection = {};
    allColumns.forEach(col => {
      newSelection[col.field] = false;
    });
    setSelectedColumns(newSelection);
  };

  const [pdfProgress, setPdfProgress] = useState(null); // { job_id, row_count }

  const _buildReportRequest = () => {
    const columns = allColumns
      .filter(col => selectedColumns[col.field])
      .map(col => ({ field: col.field, label: col.label }));

    if (columns.length === 0) return null;

    const cleanedFilters = {};
    Object.entries(reportFilters).forEach(([key, value]) => {
      if (value !== null && value !== '' && value !== undefined) {
        cleanedFilters[key] = value;
      }
    });

    return {
      format,
      title,
      columns,
      include_summary: includeSummary,
      ...cleanedFilters
    };
  };

  const _triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setPdfProgress(null);

    try {
      const reportRequest = _buildReportRequest();
      if (!reportRequest) {
        setError('Please select at least one column');
        setGenerating(false);
        return;
      }

      if (format === 'csv') {
        // CSV: streams directly — instant download
        const response = await apiClient.post('/reports/generate', reportRequest, {
          responseType: 'blob',
          timeout: 300000 // 5 min for streaming
        });

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
        _triggerDownload(new Blob([response.data]), `report_${timestamp}.csv`);
        setGenerating(false);
        onClose();
        if (onGenerate) onGenerate();

      } else if (format === 'pdf') {
        // PDF: background job — poll for completion
        const response = await apiClient.post('/reports/generate', reportRequest);
        const { job_id } = response.data;

        if (!job_id) {
          setError('Failed to start report generation');
          setGenerating(false);
          return;
        }

        setPdfProgress({ job_id, row_count: 0 });

        // Poll for completion
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await apiClient.get(`/reports/status/${job_id}`);
            const { status, row_count, error: jobError } = statusResponse.data;

            setPdfProgress(prev => ({ ...prev, row_count }));

            if (status === 'completed') {
              clearInterval(pollInterval);
              // Download the file
              const fileResponse = await apiClient.get(`/reports/download/${job_id}`, {
                responseType: 'blob'
              });
              const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
              _triggerDownload(new Blob([fileResponse.data]), `report_${timestamp}.pdf`);
              setPdfProgress(null);
              setGenerating(false);
              onClose();
              if (onGenerate) onGenerate();
            } else if (status === 'failed') {
              clearInterval(pollInterval);
              setError(jobError || 'Report generation failed');
              setPdfProgress(null);
              setGenerating(false);
            }
          } catch (pollErr) {
            clearInterval(pollInterval);
            setError('Lost connection while generating report');
            setPdfProgress(null);
            setGenerating(false);
          }
        }, 2000); // Poll every 2 seconds

      }
    } catch (err) {
      console.error('Failed to generate report:', err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Report generation timed out. Try narrowing your filters.');
      } else {
        setError(err.response?.data?.detail || 'Failed to generate report. Please try again.');
      }
      setGenerating(false);
    }
  };

  const selectedCount = Object.values(selectedColumns).filter(v => v).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TableChartIcon />
        Generate Report
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Report Settings */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Report Settings
            </Typography>
            
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend">Format</FormLabel>
              <RadioGroup
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                row
              >
                <FormControlLabel 
                  value="csv" 
                  control={<Radio />} 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TableChartIcon fontSize="small" />
                      CSV
                    </Box>
                  }
                />
                <FormControlLabel 
                  value="pdf" 
                  control={<Radio />} 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DescriptionIcon fontSize="small" />
                      PDF
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            {format === 'pdf' && (
              <TextField
                fullWidth
                label="Report Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                sx={{ mb: 2 }}
              />
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={includeSummary}
                  onChange={(e) => setIncludeSummary(e.target.checked)}
                />
              }
              label="Include Summary (total, submitted, scheduled counts)"
              sx={{ mb: 2 }}
            />

            {/* Editable Filters Section */}
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="filter-content"
                id="filter-header"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FilterListIcon fontSize="small" />
                  <Typography>Report Filters</Typography>
                  {Object.values(reportFilters).filter(v => v !== null && v !== '').length > 0 && (
                    <Chip 
                      label={`${Object.values(reportFilters).filter(v => v !== null && v !== '').length} active`}
                      size="small"
                      color="primary"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="GTIN Search"
                      value={reportFilters.gtin_search}
                      onChange={(e) => setReportFilters(prev => ({ ...prev, gtin_search: e.target.value }))}
                      placeholder="Enter GTIN..."
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={reportFilters.status}
                        label="Status"
                        onChange={(e) => setReportFilters(prev => ({ ...prev, status: e.target.value }))}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="ready">Ready</MenuItem>
                        <MenuItem value="submitted">Submitted</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      size="small"
                      options={commodityOptions.commodities || []}
                      value={reportFilters.commodity}
                      onChange={(event, newValue) => {
                        setReportFilters(prev => ({ ...prev, commodity: newValue || '', sub_commodity: '' }));
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Commodity" />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      size="small"
                      options={commodityOptions.sub_commodities?.filter(
                        sc => !reportFilters.commodity || sc.startsWith(reportFilters.commodity)
                      ) || []}
                      value={reportFilters.sub_commodity}
                      onChange={(event, newValue) => {
                        setReportFilters(prev => ({ ...prev, sub_commodity: newValue || '' }));
                      }}
                      disabled={!reportFilters.commodity}
                      renderInput={(params) => (
                        <TextField {...params} label="Sub-Commodity" />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Created After"
                      value={reportFilters.created_after}
                      onChange={(e) => setReportFilters(prev => ({ ...prev, created_after: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Created Before"
                      value={reportFilters.created_before}
                      onChange={(e) => setReportFilters(prev => ({ ...prev, created_before: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={reportFilters.is_staged === true}
                            indeterminate={reportFilters.is_staged === null}
                            onChange={(e) => {
                              const currentValue = reportFilters.is_staged;
                              let newValue;
                              if (currentValue === null) newValue = true;
                              else if (currentValue === true) newValue = false;
                              else newValue = null;
                              setReportFilters(prev => ({ ...prev, is_staged: newValue }));
                            }}
                            size="small"
                          />
                        }
                        label="Staged"
                      />
                      
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={reportFilters.is_submitted === true}
                            indeterminate={reportFilters.is_submitted === null}
                            onChange={(e) => {
                              const currentValue = reportFilters.is_submitted;
                              let newValue;
                              if (currentValue === null) newValue = true;
                              else if (currentValue === true) newValue = false;
                              else newValue = null;
                              setReportFilters(prev => ({ ...prev, is_submitted: newValue }));
                            }}
                            size="small"
                          />
                        }
                        label="Submitted"
                      />
                      
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={reportFilters.is_archived === true}
                            indeterminate={reportFilters.is_archived === null}
                            onChange={(e) => {
                              const currentValue = reportFilters.is_archived;
                              let newValue;
                              if (currentValue === null) newValue = true;
                              else if (currentValue === true) newValue = false;
                              else newValue = null;
                              setReportFilters(prev => ({ ...prev, is_archived: newValue }));
                            }}
                            size="small"
                          />
                        }
                        label="Archived"
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Button
                      size="small"
                      onClick={() => {
                        setReportFilters({
                          status: '',
                          gtin_search: '',
                          commodity: '',
                          sub_commodity: '',
                          created_after: '',
                          created_before: '',
                          is_staged: null,
                          is_submitted: null,
                          is_archived: null
                        });
                      }}
                    >
                      Clear All Filters
                    </Button>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Column Selection */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                Columns ({selectedCount} selected)
              </Typography>
              <Box>
                <Button size="small" onClick={handleSelectAll}>
                  Select All
                </Button>
                <Button size="small" onClick={handleDeselectAll}>
                  Deselect All
                </Button>
              </Box>
            </Box>
            
            <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
              <FormGroup>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>
                  Default Columns
                </Typography>
                {DEFAULT_COLUMNS.map(col => (
                  <FormControlLabel
                    key={col.field}
                    control={
                      <Checkbox
                        checked={selectedColumns[col.field] || false}
                        onChange={() => handleColumnToggle(col.field)}
                        size="small"
                      />
                    }
                    label={col.label}
                  />
                ))}
                
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>
                  Additional Columns
                </Typography>
                {ADDITIONAL_COLUMNS.map(col => (
                  <FormControlLabel
                    key={col.field}
                    control={
                      <Checkbox
                        checked={selectedColumns[col.field] || false}
                        onChange={() => handleColumnToggle(col.field)}
                        size="small"
                      />
                    }
                    label={col.label}
                  />
                ))}
              </FormGroup>
            </Box>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={generating}>
          Cancel
        </Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          disabled={generating || selectedCount === 0}
          startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
        >
          {generating
            ? (pdfProgress ? `Generating PDF... (${pdfProgress.row_count} rows)` : 'Generating...')
            : `Generate ${format.toUpperCase()}`
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportGenerationDialog;